import React, { useContext } from 'react';
import { observer } from 'mobx-react';
import s from './MainHeader.scss';
import { Box, Divider, TextButton } from '@wix/design-system';
import {
  ArrowLeft,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
} from '@wix/wix-ui-icons-common';
import { dataHooks } from '../../dataHooks';
import { Logo } from '../Logo/Logo';
import { AppContextProvider } from '../AppLoader';
import { useTranslation } from 'react-i18next';
import { useIsRtl } from '../../hooks';
import classNames from 'classnames';
import { DEFAULT_LOGO_HEIGHT } from '../../utils/presets';

export interface MainHeaderProps {
  secondaryLogoUrl?: string;
  showBackButton?: boolean;
  backButtonCallback?: Function;
  disableLogoClick?: boolean;
  headerAction?: React.ReactNode;
}

export const MainHeader: React.FC<MainHeaderProps> = observer(
  ({
    secondaryLogoUrl,
    showBackButton = true,
    backButtonCallback,
    disableLogoClick = false,
    headerAction,
  }) => {
    const {
      rootStore: {
        displayStore: {
          preset,
          isMobile,
          isWixel,
          isBackButtonAboveDialog,
          showBackButtonInHeaderOnMobile,
          mobileBackButtonMarginTop,
          mobileBackButtonMarginStart,
          mobileBackButtonIconTextGap,
          isMobileFullScreen,
        },
        onHeaderBackButtonClicked,
        navigationStore,
      },
    } = useContext(AppContextProvider);
    const { t } = useTranslation();
    const isRtl = useIsRtl();
    // Preset back-button styling (Symphony: chevron + semibold, 16/14px).
    const backButtonDesign = preset.backButtonDesign;
    const backButtonIcon = backButtonDesign?.chevronIcon ? (
      isRtl ? (
        <ChevronRight />
      ) : (
        <ChevronLeft />
      )
    ) : isRtl ? (
      <ArrowRight />
    ) : (
      <ArrowLeft />
    );
    const backButtonFontSize = isMobile
      ? backButtonDesign?.mobileFontSize
      : backButtonDesign?.fontSize;
    const showBackOnMobile = showBackButtonInHeaderOnMobile && isMobile;
    const { backButtonVisibility } = navigationStore;
    const isVisible = backButtonVisibility === 'visible';
    const isNotHidden = backButtonVisibility !== 'hidden';
    const canShow = isVisible || (isNotHidden && showBackButton);
    const showBack =
      (showBackOnMobile && canShow) ||
      (!isBackButtonAboveDialog &&
        (isVisible || (isNotHidden && showBackButton && !isMobile)));
    const logoHeight =
      (isMobile ? preset?.logo?.mobile?.height : preset?.logo?.height) ||
      DEFAULT_LOGO_HEIGHT;
    return (
      <Box
        className={classNames(s.mainHeader, {
          [s.centered]: preset?.logo?.position === 'center',
          [s.mobileFullScreen]: isMobileFullScreen,
        })}
        dataHook={dataHooks.mainHeader.container}
        verticalAlign="space-between"
        direction="vertical"
        align={secondaryLogoUrl ? 'left' : 'center'}
      >
        <Box
          className={classNames({
            [s.logosContainer]: true,
            [s.headerHeight]: preset?.logo?.position !== 'center',
          })}
          verticalAlign={isMobileFullScreen ? 'top' : 'middle'}
          align={preset?.logo?.position ? preset?.logo?.position : 'left'}
          direction="horizontal"
          width="100%"
        >
          <Logo
            logoUrl={preset.logo?.src}
            height={logoHeight}
            targetUrl={disableLogoClick ? undefined : preset?.logo?.url}
            alt={preset?.logo?.alt}
            position={preset?.logo?.position}
          />
          {secondaryLogoUrl && (
            <>
              <Box
                height={logoHeight}
                marginTop="15px"
                className={s.logoDivider}
                style={
                  preset?.logo?.dividerColor
                    ? ({
                        '--logo-divider-color': preset.logo.dividerColor,
                      } as React.CSSProperties)
                    : undefined
                }
              >
                <Divider direction="vertical" />
              </Box>
              <Logo
                logoUrl={secondaryLogoUrl}
                secondary
                position={preset?.logo?.position}
              />
            </>
          )}
          {headerAction && (
            <Box
              className={s.headerAction}
              marginInlineStart="auto"
              flex="0 0 auto"
            >
              {headerAction}
            </Box>
          )}
        </Box>
        {showBack && (
          <Box
            direction="vertical"
            className={classNames(s.backButtonContainer, {
              [s.wixel]: isWixel,
              [s.mobile]: isMobile,
            })}
            style={
              showBackOnMobile
                ? {
                    marginBlockStart: mobileBackButtonMarginTop,
                    marginInlineStart: mobileBackButtonMarginStart,
                  }
                : undefined
            }
          >
            <TextButton
              dataHook={dataHooks.app.backBtn}
              prefixIcon={backButtonIcon}
              size="medium"
              className={classNames(s.backButton, s.a11yFocus)}
              weight="normal"
              skin="dark"
              underline="none"
              onClick={() => onHeaderBackButtonClicked(backButtonCallback)}
              style={
                {
                  ...(showBackOnMobile &&
                    mobileBackButtonIconTextGap && {
                      '--wds-button-gap-medium': mobileBackButtonIconTextGap,
                    }),
                  ...(backButtonDesign?.fontWeight && {
                    fontWeight: backButtonDesign.fontWeight,
                  }),
                  ...(backButtonFontSize && { fontSize: backButtonFontSize }),
                } as React.CSSProperties
              }
            >
              {t('dialogHeader.backButton.text')}
            </TextButton>
          </Box>
        )}
      </Box>
    );
  },
);
